import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { melaScheduleApi, type ScheduleMelaBody } from '../api/modules';
import { useToast } from '../components/toast/Toast';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SERVICE_OPTIONS = ['AI', 'vaccination', 'treatment', 'deworming', 'insurance', 'tagging', 'breed_improvement'];

export function MelaScheduleModal({ open, onClose }: Props) {
  const { show } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<ScheduleMelaBody>({
    scheduled_date: '',
    district_id: 0,
    mandal_id: 0,
    lead_officer_name: '',
    target_beneficiaries: 500,
    services_planned: ['AI', 'vaccination', 'treatment'],
  });
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  const mandals = useQuery({
    enabled: open,
    queryKey: ['mela-picker-mandals'],
    queryFn: melaScheduleApi.pickerMandals,
  });
  const districts = useQuery({
    enabled: open && form.mandal_id > 0,
    queryKey: ['mela-picker-districts', form.mandal_id],
    queryFn: () => melaScheduleApi.pickerDistricts(form.mandal_id),
  });

  const schedule = useMutation({
    mutationFn: () => melaScheduleApi.schedule({
      ...form,
      notes: form.notes || undefined,
    }),
    onSuccess: (d) => {
      show(`Mela scheduled · ${d.code}`);
      setCreatedCode(d.code);
      void qc.invalidateQueries({ queryKey: ['mela-list'] });
      void qc.invalidateQueries({ queryKey: ['mela-summary'] });
    },
    onError: (err) => show(`Schedule failed: ${(err as Error).message}`),
  });

  const reset = () => {
    setForm({ scheduled_date: '', district_id: 0, mandal_id: 0, lead_officer_name: '', target_beneficiaries: 500, services_planned: ['AI', 'vaccination', 'treatment'] });
    setCreatedCode(null);
    onClose();
  };

  if (!open) return null;

  const isValid = form.scheduled_date && form.mandal_id > 0 && form.district_id > 0
    && form.lead_officer_name.length >= 2 && form.target_beneficiaries > 0
    && form.services_planned.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={reset}>
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-cream-200 flex items-center justify-between">
          <h2 className="serif-display text-xl text-navy-800 font-medium">Schedule new mela camp</h2>
          <button type="button" onClick={reset} className="text-navy-800/60 hover:text-navy-800 text-xl">×</button>
        </div>

        {createdCode ? (
          <div className="p-6 space-y-4">
            <div className="bg-green-50 border border-green-300 p-4 rounded-sm">
              <div className="text-sm font-medium text-green-900 mb-2">Camp scheduled successfully</div>
              <div className="text-xs text-green-800 mb-3">The camp has been published to the Mela calendar. Field teams will be notified automatically.</div>
              <div className="bg-white border border-green-200 rounded-sm p-3 font-numeric text-base text-navy-800 select-all">
                Camp ID: {createdCode}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={reset}
                className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-white text-sm rounded-sm"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Scheduled date *</label>
              <input
                type="date"
                value={form.scheduled_date}
                onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                min={new Date().toISOString().slice(0, 10)}
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm font-numeric"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Target beneficiaries *</label>
              <input
                type="number"
                min="1" max="10000"
                value={form.target_beneficiaries}
                onChange={(e) => setForm({ ...form, target_beneficiaries: parseInt(e.target.value, 10) || 0 })}
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm font-numeric"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Mandal *</label>
              <select
                value={form.mandal_id || ''}
                onChange={(e) => setForm({ ...form, mandal_id: parseInt(e.target.value, 10) || 0, district_id: 0 })}
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm bg-white"
              >
                <option value="">Select mandal…</option>
                {mandals.data?.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">District *</label>
              <select
                value={form.district_id || ''}
                onChange={(e) => setForm({ ...form, district_id: parseInt(e.target.value, 10) || 0 })}
                disabled={form.mandal_id === 0}
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm bg-white disabled:bg-cream-100 disabled:text-navy-800/40"
              >
                <option value="">{form.mandal_id === 0 ? 'Pick mandal first…' : districts.isLoading ? 'Loading…' : 'Select district…'}</option>
                {districts.data?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Lead officer name *</label>
              <input
                type="text"
                value={form.lead_officer_name}
                onChange={(e) => setForm({ ...form, lead_officer_name: e.target.value })}
                placeholder="e.g. Dr. Manish Verma, CVO"
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm"
              />
            </div>

            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Services planned *</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SERVICE_OPTIONS.map((s) => {
                  const checked = form.services_planned.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        services_planned: checked
                          ? form.services_planned.filter((x) => x !== s)
                          : [...form.services_planned, s],
                      })}
                      className={`text-xs px-3 py-1.5 rounded-sm transition capitalize ${
                        checked ? 'bg-saffron-500 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'
                      }`}
                    >
                      {s.replace('_', ' ')}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Notes</label>
              <textarea
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Optional · venue details, special instructions…"
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm"
              />
            </div>

            <div className="col-span-2 flex items-center justify-end gap-2 pt-2 border-t border-cream-100">
              <button type="button" onClick={reset} className="px-4 py-2 border border-cream-300 text-navy-800/80 hover:bg-cream-100 text-sm rounded-sm">
                Cancel
              </button>
              <button
                type="button"
                disabled={!isValid || schedule.isPending}
                onClick={() => schedule.mutate()}
                className="px-4 py-2 bg-saffron-500 hover:bg-saffron-600 disabled:opacity-50 text-white text-sm rounded-sm"
              >
                {schedule.isPending ? 'Scheduling…' : 'Schedule camp'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
